import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import TextField from '@mui/material/TextField'
// import IonIcon from '@reacticons/ionicons'
import cn from 'classnames'
import { decode } from 'html-entities'
import { NextPage } from 'next'
import Image from 'next/image'
import Link from 'next/link'
import React, { Dispatch, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import * as enVariables from 'src/config/config'
import {
  IEnum_Content_Contenttype,
  IInputRatingData, //IAskAILogRatingInput,
  useUpdateAskAiLogRatingMutation,
} from 'src/graphql/generated/hooks'
import { useAuthenticated } from 'src/hooks/useAuthenticated'
import { useDefaultLogo } from 'src/hooks/useDefaultLogo'
import { showToast } from 'src/store/actions/toastAction'

import { PremiumContentIcon } from './PremiumContentIcon/PremiumContentIcon'

// Utility function to decode HTML entities
function decodeHtmlEntities(text: string): string {
  return decode(text)
}

// RatingButton Component
interface RatingButtonProps {
  rating: number
  onRatingChange: (newRating: number) => void
  disabled?: boolean
}

const RatingButton: React.FC<RatingButtonProps> = ({ rating, onRatingChange, disabled }) => {
  const maxRating = 5
  const tooltips = ['Poor', 'Fair', 'Average', 'Good', 'Excellent']

  return (
    <div className="flex items-center gap-2">
      {Array.from({ length: maxRating }).map((_, index) => {
        const buttonRating = index + 1
        const isSelected = rating >= buttonRating

        return (
          <div key={buttonRating} className="relative group">
            {enVariables.ASK_AI_NEW_TAB == false && (
              <div className="absolute bottom-14 left-1/2 transform -translate-x-1/2 scale-0 rounded bg-black text-white text-xs py-1 px-2 shadow-md group-hover:scale-100 transition duration-300 ease-in-out">
                {tooltips[index]}
              </div>
            )}
            <button
              className={`text-sm transition-colors text-white bg-blue-800
                ${rating && buttonRating <= rating ? 'border-white' : 'border-blue-500 hover:text-white'}
                ${enVariables.ASK_AI_NEW_TAB && !disabled && !isSelected ? 'hover:outline hover:outline-[2px] hover:outline-blue-500 hover:opacity-100' : ''}
                ${enVariables.ASK_AI_NEW_TAB ? 'h-[25px] w-[25px]' : 'rounded w-8 h-8'}
                ${enVariables.ASK_AI_NEW_TAB && disabled && isSelected ? 'outline outline-[2px] outline-blue-500 disabled:opacity-100 disabled:hover:opacity-100 disabled:cursor-not-allowed' : ''}
                ${enVariables.ASK_AI_NEW_TAB && disabled && !isSelected ? 'opacity-50 cursor-not-allowed' : ''}
              `}
              disabled={disabled}
              onClick={() => onRatingChange(buttonRating)}
            >
              {buttonRating}
            </button>
          </div>
        )
      })}
    </div>
  )
}

// Interfaces
interface SmartSearchSources {
  description?: string | null
  logo?: string | null
  sourceUrl?: string | null
  title?: string | null
  contentType?: IEnum_Content_Contenttype | null
}

interface LoadingAnimationHrProps {
  outerDivClassName?: string
  className?: string
  result: string
  sources: SmartSearchSources[]
  id?: string
  identicalId?: number | null
  lastQuery?: string | null
}

interface CardComponentProps {
  title?: string
  description?: string
  imgUrl?: string
  linkTitle?: string
  linkUrl?: string
  type?: string
  shimRed: boolean
}

// CardComponent for displaying sources
const CardComponent: React.FC<CardComponentProps> = ({
  title = '',
  description = '',
  imgUrl = '',
  linkTitle = '',
  linkUrl = '#',
  type = '',
  shimRed = true,
}) => {
  const imgSrc = imgUrl || ''
  const [showProgress, setShowProgress] = useState(shimRed)
  const [descriptionText, setDescriptionText] = useState('')
  const [titleText, setTitleText] = useState('')
  let classProgess = showProgress ? 'shim-red' : ''
  let bg = showProgress ? 'bg-slate-400 bg-sky-500/50' : 'bg-white bg-white-500/50'
  const newSourceCard =
    enVariables.ASK_AI_NEW_TAB && !showProgress
      ? 'mb-4 rounded-lg'
      : 'mt-4 min-w-288 items-center rounded-lg overflow-hidden'

  useEffect(() => {
    const descText = description.replace(/(<([^>]+)>)/gi, '')
    const subString = descText.substring(0, 95)
    const titleDesc = linkTitle.replace(/(<([^>]+)>)/gi, '')
    const titleD = titleDesc.substring(0, 29)
    setDescriptionText(descText.length > subString.length ? subString + '...' : subString)
    setTitleText(titleDesc.length > titleD.length ? titleD + '...' : titleD)
    setShowProgress(shimRed)
    classProgess = showProgress ? 'shim-red' : ''
    bg = showProgress ? 'bg-slate-400 bg-sky-500/50' : 'bg-white bg-white-500/50'
  }, [shimRed, description, linkTitle])

  const getContentType = (contentType: string) => {
    switch (contentType) {
      case IEnum_Content_Contenttype.LandscapeArticle:
        return IEnum_Content_Contenttype.LandscapeArticle
      case IEnum_Content_Contenttype.EvaluationFramework:
        return IEnum_Content_Contenttype.EvaluationFramework
      case IEnum_Content_Contenttype.RequirementsTemplate:
        return IEnum_Content_Contenttype.RequirementsTemplate
      case IEnum_Content_Contenttype.BusinessCase:
        return IEnum_Content_Contenttype.BusinessCase
      case IEnum_Content_Contenttype.CompetitiveAnalysis:
        return IEnum_Content_Contenttype.CompetitiveAnalysis
      case IEnum_Content_Contenttype.PilotGuide:
        return IEnum_Content_Contenttype.PilotGuide
      case IEnum_Content_Contenttype.PremiumResource:
        return IEnum_Content_Contenttype.PremiumResource
      case IEnum_Content_Contenttype.ThoughtLeadership:
        return IEnum_Content_Contenttype.ThoughtLeadership
      case IEnum_Content_Contenttype.Blog:
        return IEnum_Content_Contenttype.Blog
      case IEnum_Content_Contenttype.VendorResource:
        return IEnum_Content_Contenttype.VendorResource
      case IEnum_Content_Contenttype.IndustryAnalysis:
        return IEnum_Content_Contenttype.IndustryAnalysis
      case IEnum_Content_Contenttype.BuyersGuide:
        return IEnum_Content_Contenttype.BuyersGuide
      case IEnum_Content_Contenttype.VendorAnalysis:
        return IEnum_Content_Contenttype.VendorAnalysis
      default:
        return ''
    }
  }

  const contentType = getContentType(type)

  const defaultImageUrl = imgUrl

  const shouldShowDefaultImage =
    contentType != ''
      ? contentType &&
        [
          IEnum_Content_Contenttype.Blog,
          IEnum_Content_Contenttype.IndustryAnalysis,
          IEnum_Content_Contenttype.VendorResource,
          IEnum_Content_Contenttype.KnowHow,
          IEnum_Content_Contenttype.LegaltechResourcesForLawyers,
          IEnum_Content_Contenttype.BuyersGuide,
          IEnum_Content_Contenttype.PremiumResource,
        ].includes(contentType)
      : false

  const logo = useDefaultLogo(defaultImageUrl)

  const logoContent = (() => {
    if (contentType === '') {
      if (imgUrl) {
        return (
          <Image
            className="bg-white rounded-[.5rem] shadow-[1px_1px_1px_#cccccc] flex items-center justify-center w-[25px] h-[25px] min-w-[25px] min-h-[25px]"
            src={imgUrl}
            alt={title}
            width={20}
            height={20}
            priority
          />
        )
      } else {
        if (linkUrl == '#') {
          return (
            <span className="relative w-1/4 h-[24px] bg-white rounded-[1rem] mr-1">
              <span className={'w-full absolute top-0 h-[24px] rounded-[1rem] ' + classProgess}></span>
            </span>
          )
        }
        return (
          <Image
            className="bg-white rounded-[.5rem] shadow-[1px_1px_1px_#cccccc] flex items-center justify-center w-[25px] h-[25px] min-w-[25px] min-h-[25px]"
            src={logo}
            alt={title}
            width={80}
            height={80}
            priority
          />
        )
      }
      /* return (
        <span className="relative w-1/4 h-[24px] bg-white rounded-[1rem] mr-1">
          <span className={'w-full absolute top-0 h-[24px] rounded-[1rem] ' + classProgess}></span>
        </span>
      ) */
    } else if (shouldShowDefaultImage && defaultImageUrl) {
      return (
        <Image
          className="bg-white rounded-[.5rem] shadow-[1px_1px_1px_#cccccc] flex items-center justify-center w-[25px] h-[25px] min-w-[25px] min-h-[25px]"
          src={imgUrl}
          alt={title}
          width={80}
          height={80}
          priority
        />
      )
    } else {
      if (contentType) {
        return <PremiumContentIcon contentType={contentType} size="sm" defaultImage={imgUrl} />
      }

      if (!shouldShowDefaultImage) {
        return (
          <Image
            className="bg-white rounded-[.5rem] shadow-[1px_1px_1px_#cccccc] flex items-center justify-center w-[25px] h-[25px] min-w-[25px] min-h-[25px]"
            src={logo}
            alt={title}
            width={20}
            height={20}
            priority
          />
        )
      }
      return (
        <Image
          className="bg-white rounded-[.5rem] shadow-[1px_1px_1px_#cccccc] flex items-center justify-center w-[25px] h-[25px] min-w-[25px] min-h-[25px]"
          src={imgUrl}
          alt={title}
          width={20}
          height={20}
          priority
        />
      )
    }
    /*  */
    return null
  })()

  /* const logoContent = (
    <>
      {imgUrl != '' ? (
        <Image
          className="bg-white rounded-[.5rem] drop-shadow-md flex items-center justify-center w-[35px] h-[25px] min-w-[35px] min-h-[25px]"
          src={imgUrl}
          alt={title}
          width={20}
          height={20}
          priority
        />
      ) : shouldShowDefaultImage && contentType ? (
        <PremiumContentIcon contentType={contentType} size="sm" defaultImage={imgUrl} />
      ) : (
        <span className="relative w-1/4 h-[24px] bg-white rounded-[1rem] mr-1">
          <span className={'w-full absolute top-0 h-[24px] rounded-[1rem] ' + classProgess}></span>
        </span>
      )}
    </>
  ) */

  return (
    <div className={bg + '  ' + newSourceCard}>
      <Link
        href={linkUrl}
        className="inline-flex items-center justify-center text-base text-[#011D58]"
        rel="noopener noreferrer"
        title={linkTitle}
        target="_blank"
      >
        <div
          className={cn('p-[24px] w-[288px] h-[146px] grid justify-center', {
            'border rounded-[.3rem]': enVariables.ASK_AI_NEW_TAB,
          })}
        >
          <p className="w-[239px] h-[28px] flex flex-row text-xs leading-6">
            {imgSrc && (
              <Link className="text-left  h-[25px]" href={imgSrc}>
                {logoContent}
              </Link>
            )}
            {!imgSrc && logoContent}
            <span
              className={
                'ml-1 relative flex items-center w-[205px] h-[25px] text-[15px] font-primary font-regular leading-5 rounded-[5px] bg-[#F1F3F5] text-[#011D58] hover:underline'
              }
            >
              <span className={'ml-2 block truncate whitespace-nowrap overflow-hidden rounded-[1rem]' + classProgess}>
                {titleText}
              </span>
            </span>
          </p>
          <p className="relative w-[240px] h-[65px] bg-white">
            <span
              className={
                'w-[240px] absolute top-0 h-[65px] text-[16px] text-[#767676] leading-[22px] font-primary font-regular line-clamp-3' +
                classProgess
              }
            >
              {descriptionText}
            </span>
          </p>
        </div>
      </Link>
    </div>
  )
}

function decodeJWT(token: any) {
  try {
    const base64Url = token.split('.')[1]
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(function (c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
        })
        .join(''),
    )

    return JSON.parse(jsonPayload)
  } catch (e) {
    return null
  }
}

// Main LoadingAnimationHr Component
export const LoadingAnimationHr: NextPage<LoadingAnimationHrProps> = ({
  className,
  outerDivClassName,
  result,
  sources,
  id,
  identicalId,
  lastQuery = null,
}) => {
  const [resultHeight, setResultHeight] = useState('h-[100px]')
  const [classProgess, setClassProgess] = useState('shim-red')
  const [showRating, setShowRating] = useState(result ? true : false)
  const [showFeedbackDialog, setShowFeedbackDialog] = useState(false)
  const [selectedRating, setSelectedRating] = useState<number | null>(null)
  const [feedback, setFeedback] = useState('')
  const [ratingSubmitted, setRatingSubmitted] = useState(false)

  const [updateRating] = useUpdateAskAiLogRatingMutation()
  const { session } = useAuthenticated()
  const userId = session?.user.id
  const token = session?.user?.idToken
  const userEmail = decodeJWT(token)?.email
  const isLegalTechTeam = userEmail?.endsWith('@impressico.com') || userEmail?.endsWith('@legaltechnologyhub.com')
  const dispatch: Dispatch<any> = useDispatch()
  let bg = !result ? 'bg-slate-400 bg-sky-500/50' : 'bg-white bg-white-500/50'
  const whiteRatingText = enVariables.ASK_AI_NEW_TAB ? '' : 'text-white'
  const outerBorder = enVariables.ASK_AI_NEW_TAB == false ? 'p-2' : 'p-8 pr-[150px] mb-2 border h-full'
  const bottomMargin = enVariables.ASK_AI_NEW_TAB ? '' : 'mb-4'
  // Update UI based on result availability
  useEffect(() => {
    setClassProgess('shim-red')
    setResultHeight('h-[100px]')
    bg = !result ? 'bg-slate-400 bg-sky-500/50' : 'bg-white bg-white-500/50'
    if (result) {
      setClassProgess('')
      setResultHeight('h-full')
      setShowRating(true)
    }
  }, [result])

  // Format result text with basic markdown-like syntax
  const formattedText = result
    .replace(/\n/g, '<br>')
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/### (.*?)\n/g, '<h3>$1</h3>')
    .replace(/## (.*?)\n/g, '<h2>$1</h2>')
    .replace(/# (.*?)\n/g, '<h1>$1</h1>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/###/g, '')
    .replace(/#/g, '')

  // Submit rating and feedback to the backend
  const submitRating = async (rating: number, feedback: string) => {
    try {
      const req: IInputRatingData = {
        rating,
        id,
        userID: userId,
        identicalId: identicalId || null,
        feedback,
        email: userEmail || '',
      }

      const res = await updateRating({ variables: { data: req } })
      const success = res?.data?.updateAskAILogRating?.success
      const message = res?.data?.updateAskAILogRating?.message || 'Rating updated successfully'

      setShowRating(!success)
      setRatingSubmitted(!!success)
      dispatch(showToast(message, success ? 'success' : 'Failed to update rating'))
    } catch (error) {
      dispatch(showToast('Failed to update rating', 'error'))
      setRatingSubmitted(false)
    }
  }

  // Handle rating selection
  const handleRatingChange = (newRating: number) => {
    setSelectedRating(newRating)

    if (isLegalTechTeam && enVariables.ASK_AI_NEW_TAB == false) {
      setShowFeedbackDialog(true)
    } else {
      submitRating(newRating, '')
    }
  }

  return (
    <div className={cn(outerDivClassName, 'flex flex-col items-center text-[16px]  font-secondary')}>
      {/* Result Display */}
      <div className={'flex flex-row space-x-4' + ' ' + bottomMargin}>
        <div className={bg + ' p-4 flex flex-col items-center rounded-lg overflow-hidden'}>
          {lastQuery && lastQuery !== '' && lastQuery !== null && (
            <p className={'w-[904px] mb-4 pl-8 pr-8 pt-2 pb-2 bg-clouds ' + resultHeight + ' ' + classProgess}>
              <strong>{lastQuery}</strong>
            </p>
          )}

          <p
            className={
              'font-primary text-[16px] text-[#333333] leading-[22px] w-[904px] bg-white rounded-[1rem] mb-2 ' +
              resultHeight +
              ' ' +
              classProgess +
              ' ' +
              outerBorder
            }
            dangerouslySetInnerHTML={{ __html: formattedText }}
          />
        </div>
      </div>

      {/* Sources Display */}
      <div className={cn(className, 'w-full justify-evenly mb-4')}>
        {sources.length > 0 ? (
          <>
            {sources.map((item: any) => (
              <CardComponent
                title={item?.title}
                linkUrl={item?.sourceUrl}
                linkTitle={item?.title}
                description={decodeHtmlEntities(item?.description || '')}
                imgUrl={item?.logo || ''}
                type={item?.contentType?.replace(/ /g, '_')}
                shimRed={item ? false : true}
              />
            ))}
          </>
        ) : result && sources.length == 0 ? (
          ''
        ) : (
          <>
            <CardComponent shimRed={true} />
            <CardComponent shimRed={true} />
            <CardComponent shimRed={true} />
          </>
        )}
      </div>

      {/* Rating Section */}
      {sources.length !== 0 &&
        (enVariables.ASK_AI_NEW_TAB && result ? (
          <div className="mb-4 flex items-center space-x-4">
            <h3 className={'text-xl font-semibold' + ' ' + whiteRatingText}>Rate this answer:</h3>
            <RatingButton rating={selectedRating ?? 0} onRatingChange={handleRatingChange} disabled={ratingSubmitted} />
          </div>
        ) : (
          showRating && (
            <div className="mb-4 flex items-center space-x-4">
              <h3 className={'text-xl font-semibold' + ' ' + whiteRatingText}>Rate this answer:</h3>
              <RatingButton rating={0} onRatingChange={handleRatingChange} />
            </div>
          )
        ))}

      {/* Feedback Dialog for Legal Tech Hub Team */}
      <Dialog
        open={showFeedbackDialog}
        onClose={() => setShowFeedbackDialog(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { width: '80vw', height: '70vh', maxWidth: 'none', p: 2 },
        }}
      >
        <DialogTitle>Provide Feedback</DialogTitle>
        <DialogContent sx={{ pb: 0 }}>
          {' '}
          {/* Removed bottom padding */}
          <TextField
            autoFocus
            margin="dense"
            label="Feedback"
            type="text"
            fullWidth
            multiline
            rows={10}
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', p: 3 }}>
          <Button onClick={() => setShowFeedbackDialog(false)} variant="outlined" sx={{ px: 3, py: 1 }}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (selectedRating !== null) {
                submitRating(selectedRating, feedback)
              }
              setShowFeedbackDialog(false)
              setFeedback('')
            }}
            variant="contained"
            color="primary"
            sx={{ px: 4, py: 1, fontWeight: 'bold' }}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}
